import {
  styled,
  Button as MuiButton,
  CircularProgress,
  Box,
} from '@mui/material';

interface BaseButtonStyledProps {
  $isLoading?: boolean;
  $isEndIconVisible: boolean;
  $isStartIconVisible: boolean;
}

export const BaseButtonStyled = styled(MuiButton)<BaseButtonStyledProps>(
  ({ theme, $isLoading, $isStartIconVisible, $isEndIconVisible }) => ({
    minHeight: '4rem',
    maxHeight: '7.2rem',
    position: 'relative',
    wordBreak: 'break-word',

    [`${LabelStyled}`]: {
      visibility: $isLoading ? 'hidden' : 'visible',
    },

    '.MuiSvgIcon-root': {
      fontSize: '1.6rem',
    },
    '.MuiButton-startIcon': {
      marginRight: theme.spacing(1),
      ...($isLoading && {
        right: '50%',
        position: 'absolute',
        transform: $isEndIconVisible ? 'translateX(-50%)' : '',
      }),
    },
    '.MuiButton-endIcon': {
      marginLeft: theme.spacing(1),
      ...($isLoading && {
        left: '50%',
        position: 'absolute',
        transform: $isStartIconVisible ? 'translateX(50%)' : '',
      }),
    },
    ':not(:first-of-type)': {
      marginLeft: 0, // override MUI-style
    },
    pointerEvents: 'all',
  })
);

export const LabelStyled = styled('span')({});

export const PrimaryButtonStyled = styled(BaseButtonStyled)(({ theme }) => ({
  padding: theme.spacing(3, 5),
  '&:disabled': {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[400],
  },
  '&:focus': {
    outline: `0.2rem solid ${theme.palette.primary.main}`,
  },
  '.MuiTouchRipple-child': {
    backgroundColor: theme.palette.primary.pressed,
  },
}));

export const SecondaryButtonStyled = styled(BaseButtonStyled)(({ theme }) => ({
  border: `0.1rem solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  padding: theme.spacing(3, 5),
  '&:disabled': {
    border: `0.1rem solid ${theme.palette.grey[400]}`,
    color: theme.palette.grey[400],
  },
  '&:focus': {
    border: '0.1rem solid transparent',
    outline: `0.2rem solid ${theme.palette.primary.main}`,
  },
  '.MuiTouchRipple-child': {
    backgroundColor: theme.palette.grey[200],
  },
}));

export const TertiaryButtonStyled = styled(BaseButtonStyled)(({ theme }) => ({
  padding: theme.spacing(3, 0),
  minWidth: 0,
  '&:disabled': {
    color: theme.palette.grey[400],
  },
  '&:focus': {
    backgroundColor: theme.palette.grey[200],
  },
  '.MuiTouchRipple-child': {
    backgroundColor: theme.palette.grey[200],
  },
}));

const getLoaderTranslateX = (
  $isStartIconVisible: boolean,
  $isEndIconVisible: boolean
) => {
  if ($isEndIconVisible && !$isStartIconVisible) {
    return 'translateX(-100%)';
  }

  if (!$isEndIconVisible && $isStartIconVisible) {
    return '';
  }

  return 'translateX(-50%)';
};

interface LoaderWrapperStyledProps {
  $isStartIconVisible: boolean;
  $isEndIconVisible: boolean;
}

export const LoaderWrapperStyled = styled(Box)<LoaderWrapperStyledProps>(
  ({ $isStartIconVisible, $isEndIconVisible }) => ({
    display: 'flex',
    left: '50%',
    position: 'absolute',
    transform: getLoaderTranslateX($isStartIconVisible, $isEndIconVisible),
  })
);

export const CircularProgressStyled = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primaryDark.main,
}));
